import { useMemo } from 'react';

import { useTranslation } from 'libs/i18n';

import { Icon } from '../Icon';
import { LinkLabel } from '../LinkLabel';
import { Pressed } from '../Pressed';
import { Stack } from '../Stack';
import { Typography } from '../Typography';

import { FileContainer } from './FileContainer';
import {
  ContainerStyled,
  DragActiveStyled,
  LineStyled,
  StyledStack,
} from './FilesUpload.styles';
import { FilesUploadProps } from './types';
import { useFilesUpload } from './useFilesUpload';

/**
 * UploadFiles component.
 */
export const FilesUpload = (props: FilesUploadProps) => {
  const { t } = useTranslation();

  const { files, name, onRemoveFile, isError, className, title, subtitle } =
    props;

  const {
    getInputProps,
    getRootProps,
    isDragAccept,
    isDragReject,
    isDragActive,
    isFocused,
    open,
    setShowFiles,
    showFiles,
    addDisabled,
  } = useFilesUpload(props);

  const filesContainer = useMemo(
    () =>
      files?.map((file, index) => (
        <FileContainer
          key={file.id}
          file={file}
          onRemoveFile={() => onRemoveFile?.(file, index)}
        />
      )),
    [files, onRemoveFile]
  );

  return (
    <ContainerStyled
      isError={isError}
      showFiles={showFiles}
      {...getRootProps({ isFocused, isDragAccept, isDragReject, className })}
    >
      <input {...getInputProps()} name={name} />

      {isDragActive ? (
        <DragActiveStyled>
          <Icon.CloudUpload size={48} />
          <Typography>{t('components.filesUpload.dragactiveTitle')}</Typography>
        </DragActiveStyled>
      ) : (
        <Stack className="filesUpload-content" spacing={16}>
          <Stack
            alignItems="center"
            direction="row"
            justifyContent="space-between"
          >
            <StyledStack spacing={8}>
              <Typography className="filesUpload-title" variant="bodySemiBold">
                {title}
              </Typography>
              {subtitle && <Typography variant="body">{subtitle}</Typography>}

              <LinkLabel
                as="button"
                disabled={addDisabled}
                label={t('common.add')}
                mode="text"
                onClick={open}
              />
            </StyledStack>
            {!!files?.length && (
              <Pressed onClick={() => setShowFiles((v) => !v)}>
                <Icon.ArrowDown className="filesUpload-arrowFile" />
              </Pressed>
            )}
          </Stack>
          {!!files?.length && <LineStyled />}
          {showFiles && !!files?.length && (
            <Stack datax="documents" spacing={32}>
              {filesContainer}
            </Stack>
          )}
        </Stack>
      )}
    </ContainerStyled>
  );
};
